/**
 * Plan 13-02 — CheckerHistoryPage
 *
 * Route: /logsheets/checker/history
 * Permission gate: logsheet.checker.read-history
 *
 * Paginated list of entries this checker has Checked or Rejected.
 * Filters: status (Checked|Rejected|both), date range.
 * Row click opens a read-only detail drawer (re-uses CheckerReviewPage via navigation).
 *
 * Mirrors MakerLogsheetsListPage style (Phase 12 Plan 12-03).
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCheckerHistoryQuery } from '@/hooks/useLogsheetCheckerQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { HistoryItem, HistoryListQuery } from '@/types/logsheet-checker';

const PAGE_SIZE = 20;

type HistoryStatusFilter = 'Checked' | 'Rejected' | 'all';

function historyStatusVariant(s: 'Checked' | 'Rejected'): 'default' | 'destructive' {
  return s === 'Rejected' ? 'destructive' : 'default';
}

export function CheckerHistoryPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status');
  const initialStatus: HistoryStatusFilter =
    statusParam === 'Checked' || statusParam === 'Rejected' ? statusParam : 'all';
  const initialFromDate = searchParams.get('fromDate') ?? '';
  const initialToDate = searchParams.get('toDate') ?? '';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [statusFilter, setStatusFilter] = useState<HistoryStatusFilter>(initialStatus);
  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [page, setPage] = useState<number>(initialPage);

  useEffect(() => {
    const next = new URLSearchParams();
    if (statusFilter !== 'all') next.set('status', statusFilter);
    if (fromDate !== '') next.set('fromDate', fromDate);
    if (toDate !== '') next.set('toDate', toDate);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [statusFilter, fromDate, toDate, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, fromDate, toDate]);

  const listQueryArgs: HistoryListQuery = useMemo(() => {
    const out: { -readonly [K in keyof HistoryListQuery]: HistoryListQuery[K] } = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (statusFilter !== 'all') out.status = statusFilter;
    if (fromDate !== '') out.fromDate = fromDate;
    if (toDate !== '') out.toDate = toDate;
    return out;
  }, [statusFilter, fromDate, toDate, page]);

  const listQuery = useCheckerHistoryQuery(listQueryArgs);

  const columns = useMemo<ColumnDef<HistoryItem>[]>(
    () => [
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.entryDate}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) => <span>{row.original.siteName}</span>,
      },
      {
        accessorKey: 'machineName',
        header: 'Machine',
        cell: ({ row }) => (
          <span>
            {formatMachineName(
              row.original.machineName,
              row.original.vehicleNumber,
              row.original.machineCode,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'makerName',
        header: 'Maker',
        cell: ({ row }) => <span>{row.original.makerName}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={historyStatusVariant(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'actedAt',
        header: 'Acted At',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.actedAt !== null
              ? new Date(row.original.actedAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const table = useReactTable({
    data: data as HistoryItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.checker.read-history')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6" data-testid="checker-history-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Checker History</h1>
        <p className="text-muted-foreground">Entries you have checked or rejected.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>Filter by status or date range.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1">
              <Label htmlFor="checker-history-status">Status</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  if (v === 'Checked' || v === 'Rejected' || v === 'all') {
                    setStatusFilter(v);
                  }
                }}
              >
                <SelectTrigger
                  id="checker-history-status"
                  data-testid="checker-history-filter-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="checker-history-filter-status-all">
                    All
                  </SelectItem>
                  <SelectItem value="Checked" data-testid="checker-history-filter-status-Checked">
                    Checked
                  </SelectItem>
                  <SelectItem value="Rejected" data-testid="checker-history-filter-status-Rejected">
                    Rejected
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="checker-history-from-date">From date</Label>
              <Input
                id="checker-history-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                data-testid="checker-history-filter-from-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="checker-history-to-date">To date</Label>
              <Input
                id="checker-history-to-date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                data-testid="checker-history-filter-to-date"
              />
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="checker-history-loading">
              Loading history…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="checker-history-error">
              Failed to load history.
            </p>
          )}

          {listQuery.data && (
            <>
              <div data-testid="checker-history-table">
                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {table.getRowModel().rows.length === 0 ? (
                    <p className="text-sm text-center py-8 text-muted-foreground" data-testid="checker-history-empty">
                      No history entries.
                    </p>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(`/logsheets/view/${String(row.original.id)}`);
                        }}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <Badge variant={historyStatusVariant(row.original.status)}>{row.original.status}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Date: </span><span className="font-mono">{row.original.entryDate}</span></div>
                          <div><span className="font-medium text-foreground">Site: </span>{row.original.siteName}</div>
                          <div><span className="font-medium text-foreground">Maker: </span>{row.original.makerName}</div>
                          <div><span className="font-medium text-foreground">Acted: </span><span className="font-mono">{row.original.actedAt !== null ? new Date(row.original.actedAt).toLocaleDateString() : '—'}</span></div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((h) => (
                            <TableHead key={h.id}>
                              {h.isPlaceholder
                                ? null
                                : flexRender(h.column.columnDef.header, h.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="text-center py-8">
                            <span data-testid="checker-history-empty">No history entries.</span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`checker-history-row-${String(row.original.id)}`}
                            onClick={() => {
                              void navigate(`/logsheets/view/${String(row.original.id)}`);
                            }}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Page {page} of {pageCount} · {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="checker-history-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="checker-history-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
